// Product reviews slideout
//
// Renders Yotpo reviews inside the theme's slideout panel, so they carry the theme's
// design rather than Yotpo's widget styling. The panel shell and all of its copy live
// in snippets/product-reviews-slideout.liquid; this fills in the summary and the list.
//
// Nothing is fetched until the panel is opened for the first time.

const API_ORIGIN = 'https://api-cdn.yotpo.com';

// Writing a review. These three are the same calls Yotpo's own widget makes, read out of
// widget-reviews-main-widget, and none of them takes an auth token — the app key at the top of
// snippets/product-reviews-slideout.liquid is the only credential involved, and it is public.
//
// Note the two hosts. Creating a review goes to api.yotpo.com, while processing the images goes
// to api-cdn.yotpo.com, which is not what Yotpo's API reference says. Their reference documents
// the server-to-server variants of both: those want an X-Yotpo-Token (a secret account token,
// unshippable in theme JS) and, for images, URLs that are already publicly hosted. What the
// widget does instead is below, and it is all reachable from a browser.
const CREATE_REVIEW_URL = 'https://api.yotpo.com/v1/widget/reviews';
const IMAGE_POLICY_URL = 'https://api.yotpo.com/s3_signature';
const IMAGE_PROCESS_URL = `${API_ORIGIN}/images/process`;

// Fixed fields of the S3 form post. The UUID is a constant in Yotpo's widget, not an identifier
// of anything of ours, and the policy is signed against it, so it has to be sent verbatim
const S3_ALGORITHM = 'AWS4-HMAC-SHA256';
const S3_META_UUID = '14365123651274';

// Yotpo caps a review at 4 photos. The size limit and the "image/" content type are conditions of
// the signed policy itself, so a file over the limit is rejected by S3 rather than by Yotpo
const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

// Phone photos routinely arrive at 4000px and several megabytes, and nothing in the panel ever
// displays one larger than the lightbox. Downscaling before upload is the difference between a
// review with photos submitting in seconds and in minutes on a phone
const MAX_PHOTO_EDGE = 2048;
const PHOTO_QUALITY = 0.85;

// The four sort options in the panel, mapped onto the API's "sortings" array. "media" is
// the default the widget itself opens with: it sorts reviews with photos to the top
// rather than filtering the rest out
const SORTINGS = {
	media: [
		{ sort_by: 'grouping', ascending: true },
		{ sort_by: 'images', ascending: false },
	],
	recent: [{ sort_by: 'date', ascending: false }],
	highest: [{ sort_by: 'rating', ascending: false }],
	lowest: [{ sort_by: 'rating', ascending: true }],
};

const STAR_COUNT = 5;

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

// Yotpo serves each review photo at a few sizes under the same path. "original" is whatever the
// customer uploaded, which is often a many-megabyte phone photo; "medium" is a proportional
// resize (640px on the long edge) that is plenty for the lightbox and a fraction of the weight.
// Not every image has one, so this is only ever a first choice to try
const ORIGINAL_IMAGE = /\/original(\.\w+)(\?|$)/;

function previewImageUrl(url) {
	return ORIGINAL_IMAGE.test(url) ? url.replace(ORIGINAL_IMAGE, '/medium$1$2') : null;
}

// Whether a size actually exists. This cannot be inferred from loading it: Yotpo answers a
// missing size with a 404 whose body is still a valid image — a 400x400 grey placeholder — so
// the browser loads it without complaint and the customer would get the placeholder instead of
// their photo. Only the status tells them apart, and the CDN sends access-control-allow-origin,
// so a HEAD can read it. HEAD returns no body, so this costs very little
async function imageExists(url) {
	try {
		const response = await fetch(url, { method: 'HEAD' });

		return response.ok;
	} catch (error) {
		return false; // Offline, blocked, or CORS withdrawn: fall back to the original
	}
}

// Resolves to the loaded size, or null if the image could not be loaded at all.
//
// This deliberately does not use decode(), which reads better here but never settles while the
// page is hidden: tapping a photo and switching tabs before it loaded left the panel stuck
// waiting forever. The load event fires either way.
async function loadImage(src) {
	const image = new Image();

	const loaded = await new Promise((resolve) => {
		image.onload = () => resolve(true);
		image.onerror = () => resolve(false);
		image.src = src;
	});

	return loaded && image.naturalWidth && image.naturalHeight
		? { src, width: image.naturalWidth, height: image.naturalHeight }
		: null;
}

// S3 keys end up in the public URL that gets handed back to Yotpo, so anything that would need
// escaping there is flattened first. The extension is kept: the policy requires an image content
// type and Yotpo reads the rest by extension
function safeFileName(name) {
	return String(name)
		.replace(/[^a-zA-Z0-9.]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(-80) || 'photo.jpg';
}

// Shrinks a photo to something sensible for a review, returning the original untouched if there
// is nothing to gain or if the browser cannot decode it.
//
// GIFs are always left alone: a canvas only ever sees the first frame, so re-encoding one would
// silently drop the animation.
async function preparePhoto(file) {
	const original = { blob: file, name: safeFileName(file.name), type: file.type };

	if (file.type === 'image/gif' || typeof createImageBitmap !== 'function') {
		return original;
	}

	const bitmap = await createImageBitmap(file).catch(() => null);

	if (!bitmap) {
		return original;
	}

	const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(bitmap.width, bitmap.height));

	if (scale === 1) {
		bitmap.close();

		return original;
	}

	const canvas = document.createElement('canvas');

	canvas.width = Math.round(bitmap.width * scale);
	canvas.height = Math.round(bitmap.height * scale);
	canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
	bitmap.close();

	const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY));

	if (!blob) {
		return original;
	}

	return {
		blob,
		name: safeFileName(file.name.replace(/\.\w+$/, '')) + '.jpg',
		type: 'image/jpeg',
	};
}

export function formatDate(value) {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

class ProductReviews extends HTMLElement {
	connectedCallback() {
		if (this.hasAttribute('ready')) {
			return;
		}

		this.setAttribute('ready', '');

		this.cache = new Map();
		this.state = { search: '', score: '', sort: 'media', page: 1 };
		this.reviews = [];
		this.total = 0;

		this.mode = 'list';
		this.photos = [];

		this.reading = this.querySelector('[data-reading]');
		this.summary = this.querySelector('[data-summary]');
		this.list = this.querySelector('[data-list]');
		this.status = this.querySelector('[data-status]');
		this.loadMoreButton = this.querySelector('[data-load-more]');
		this.searchForm = this.querySelector('[data-search-form]');
		this.searchInput = this.searchForm.querySelector('input');
		this.scoreFilter = this.querySelector('[data-score-filter]');
		this.sortControl = this.querySelector('[data-sort]');
		this.countLabel = this.drawer?.querySelector('[data-reviews-count]');

		this.searchForm.addEventListener('submit', (event) => {
			event.preventDefault();
			this.apply({ search: this.searchInput.value.trim() });
		});

		// A cleared search field should go straight back to the full list, whether it was
		// cleared with the native × or by deleting the text
		this.searchInput.addEventListener('search', () => {
			if (this.searchInput.value.trim() === '') {
				this.apply({ search: '' });
			}
		});

		this.scoreFilter.addEventListener('change', () => this.apply({ score: this.scoreFilter.value }));
		this.sortControl.addEventListener('change', () => this.apply({ sort: this.sortControl.value }));

		this.loadMoreButton.addEventListener('click', () => this.loadPage(this.state.page + 1));

		this.list.addEventListener('click', (event) => {
			const thumbnail = event.target.closest('[data-review-image]');

			if (thumbnail) {
				this.openLightbox(thumbnail);
			}
		});

		this.setupForm();

		// Loaded on page load rather than on first open, because the reviews also feed the
		// product's structured data. Disabling Yotpo's widget took their rich snippets with
		// it, so the rating and reviews have to be back in the markup whether or not anyone
		// opens the panel. It doubles as a warm cache, so the panel opens with no wait
		this.load();
	}

	get drawer() {
		return this.closest('x-drawer');
	}

	get appKey() {
		return this.getAttribute('app-key');
	}

	get productId() {
		return this.getAttribute('product-id');
	}

	get perPage() {
		return Number(this.getAttribute('per-page')) || 15;
	}

	get productTitle() {
		return this.getAttribute('product-title') || '';
	}

	get productUrl() {
		return this.getAttribute('product-url') || '';
	}

	load() {
		if (this.loaded) {
			return;
		}

		this.loaded = true;
		this.loadBottomline();
		this.loadPage(1);
	}

	// A change to any control resets to the first page
	apply(changes) {
		Object.assign(this.state, changes);
		this.loadPage(1);
	}

	async loadPage(page) {
		const append = page > 1;

		this.abortController?.abort();
		this.abortController = new AbortController();

		this.setBusy(true, append);

		try {
			const response = await this.fetchReviews(page, this.abortController.signal);

			this.state.page = page;
			this.total = response.pagination?.total ?? 0;
			this.reviews = append ? this.reviews.concat(response.reviews || []) : response.reviews || [];

			// Only the first, unfiltered page feeds the structured data. Filtering to one star
			// must not rewrite the page's markup to claim those are all the reviews there are
			if (!this.structuredDataReviews) {
				this.structuredDataReviews = response.reviews || [];
				this.updateStructuredData();

				// Announced for anything else on the page built on these reviews — the trusted
				// reviews section under the product reads them from here rather than asking Yotpo
				// for the same thing again. Fired from inside this guard on purpose: it carries
				// the unfiltered page, so a visitor filtering the panel to one star cannot rewrite
				// what the rest of the page is showing
				this.dispatchEvent(
					new CustomEvent('product-reviews:loaded', {
						bubbles: true,
						detail: { productId: this.productId, reviews: this.structuredDataReviews },
					})
				);
			}

			this.render(response);
		} catch (error) {
			if (error.name === 'AbortError') {
				return; // A newer request is already in flight
			}

			this.renderError();
		} finally {
			this.setBusy(false, append);
		}
	}

	async fetchReviews(page, signal) {
		const payload = {
			domain_key: this.productId,
			sortings: SORTINGS[this.state.sort] || SORTINGS.media,
			scores: this.state.score ? [Number(this.state.score)] : [],
		};

		if (this.state.search) {
			payload.free_text_search = this.state.search;
		}

		const key = `${page}:${JSON.stringify(payload)}`;

		if (this.cache.has(key)) {
			return this.cache.get(key);
		}

		const url = `${API_ORIGIN}/v1/reviews/${this.appKey}/filter.json?page=${page}&per_page=${this.perPage}`;
		const request = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
			signal,
		});

		if (!request.ok) {
			throw new Error(`Yotpo responded with ${request.status}`);
		}

		const { response } = await request.json();

		this.cache.set(key, response);

		return response;
	}

	// The summary sits outside the filtered list: it always describes the product as a whole,
	// so it is fetched once and left alone
	async loadBottomline() {
		try {
			const url = `${API_ORIGIN}/v1/widget/${this.appKey}/products/${this.productId}/reviews.json?per_page=1`;
			const request = await fetch(url);

			if (!request.ok) {
				return;
			}

			const { response } = await request.json();

			this.renderSummary(response.bottomline);
			this.revealFirstReviewTrigger(response.bottomline);
		} catch (error) {
			// The list is the point of the panel, so a missing summary is not worth surfacing
		}
	}

	// Yotpo's star rating widget renders nothing on a product with no reviews, so on those the
	// panel has no trigger at all. The theme renders its own in product-info.liquid, hidden until
	// the bottomline confirms there is nothing to read, and it opens straight onto the form.
	//
	// Kept the other way round on purpose: the trigger is revealed rather than hidden, so a
	// product that does have reviews never flashes an invitation that then disappears
	revealFirstReviewTrigger(bottomline) {
		if (bottomline?.total_review) {
			return;
		}

		const trigger = document.querySelector(`[data-reviews-write="${this.productId}"]`);

		if (!trigger) {
			return;
		}

		trigger.hidden = false;
		trigger.setAttribute('aria-haspopup', 'dialog');
		// The switch waits for the drawer to finish opening. The drawer moves focus itself on the
		// way in, so a mode set before that lands its own focus first and then has it taken away,
		// which leaves a keyboard user on the panel with focus back on the body
		trigger.addEventListener('click', () => {
			this.drawer?.addEventListener('dialog:after-show', () => this.setMode('form'), { once: true });
			this.drawer?.show();
		});
	}

	// Review photos open in the same PhotoSwipe lightbox as the product gallery, rather than in
	// a new tab. <product-gallery> builds it lazily and registers the theme's own close and
	// arrow buttons on it, and loadAndOpen() takes any data source, so the whole thing is
	// borrowed as-is and the lightbox looks identical to the one on the main images.
	//
	// Yotpo does not tell us how big its photos are and PhotoSwipe needs real dimensions to size
	// a slide, so each one is loaded first, preferring the lighter "medium" size and falling back
	// to the original. If neither loads there is nothing to show, and the image opens in a tab as
	// it used to.
	//
	// No "msrc" is passed. That is PhotoSwipe's low-resolution placeholder, and the only small
	// version Yotpo offers is a square centre crop, which it then stretches to the shape of the
	// full image: a photo visibly distorted for a moment before snapping to its real proportions.
	// Loading the photo above already puts it in cache, so PhotoSwipe renders it at the right
	// proportions straight away and there is nothing for a placeholder to cover.
	async openLightbox(thumbnail) {
		const gallery = document.querySelector('product-gallery');
		const thumbnails = Array.from(thumbnail.closest('.product-reviews__images').children);

		if (!gallery || this.hasAttribute('lightbox-loading')) {
			window.open(thumbnail.dataset.reviewImage, '_blank', 'noopener');

			return;
		}

		this.setAttribute('lightbox-loading', '');

		let dataSource;

		try {
			dataSource = await Promise.all(
				thumbnails.map(async (button) => {
					const image = button.querySelector('img');
					const original = button.dataset.reviewImage;
					const preview = previewImageUrl(original);
					const candidate = preview && (await imageExists(preview)) ? preview : original;
					const loaded =
						(await loadImage(candidate)) || (candidate === original ? null : await loadImage(original));

					return {
						thumbnailElement: image,
						src: loaded ? loaded.src : original,
						width: loaded ? loaded.width : 0,
						height: loaded ? loaded.height : 0,
						alt: image.alt,
						thumbCropped: true,
					};
				})
			);
		} finally {
			// Never leave the panel stuck mid-load: the attribute also gates further clicks
			this.removeAttribute('lightbox-loading');
		}

		if (dataSource.some((slide) => !slide.width || !slide.height)) {
			window.open(thumbnail.dataset.reviewImage, '_blank', 'noopener');

			return;
		}

		this.openInGalleryLightbox(gallery, dataSource, thumbnails.indexOf(thumbnail));
	}

	// The panel traps focus, and the lightbox opens outside it, so an Escape or a click on the
	// lightbox's own close button reads as "left the panel" and closes that too. Pausing the trap
	// for as long as the lightbox is up is what focus-trap provides for exactly this case
	openInGalleryLightbox(gallery, dataSource, index) {
		const trap = this.drawer?.focusTrap;
		const lightbox = gallery.lightBox;

		const resume = () => {
			lightbox.off('destroy', resume);
			trap?.unpause();
		};

		try {
			trap?.pause();
			lightbox.on('destroy', resume);
			lightbox.loadAndOpen(index, dataSource);
		} catch (error) {
			resume();
			window.open(dataSource[index].src, '_blank', 'noopener');
		}
	}

	// Yotpo's reviews widget carried the product's review rich snippets, and disabling it took
	// them with it: Shopify's own structured_data filter only knows about the native review
	// metafields, which this store does not use. So the rating and the reviews are written back
	// into the Product node the theme already outputs, rather than added as a second node, which
	// keeps one complete Product in the markup for crawlers to read.
	//
	// Both halves arrive from separate requests, so this runs on whichever lands first and again
	// on the second.
	updateStructuredData() {
		if (!this.bottomline?.total_review) {
			return; // A rating needs reviews behind it to be worth publishing
		}

		const script = Array.from(document.querySelectorAll('script[type="application/ld+json"]')).find((element) => {
			try {
				return JSON.parse(element.textContent)['@type'] === 'Product';
			} catch (error) {
				return false;
			}
		});

		if (!script) {
			return;
		}

		const data = JSON.parse(script.textContent);

		data.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: Number(this.bottomline.average_score).toFixed(1),
			reviewCount: String(this.bottomline.total_review),
			bestRating: String(STAR_COUNT),
			worstRating: '1',
		};

		const reviews = (this.structuredDataReviews || [])
			.filter((review) => review.content && review.user?.display_name)
			.map((review) => {
				const entry = {
					'@type': 'Review',
					reviewRating: {
						'@type': 'Rating',
						ratingValue: String(review.score),
						bestRating: String(STAR_COUNT),
						worstRating: '1',
					},
					author: { '@type': 'Person', name: review.user.display_name },
					reviewBody: review.content,
				};

				const title = this.reviewTitle(review);

				if (title) {
					entry.name = title;
				}

				if (review.created_at) {
					entry.datePublished = review.created_at.slice(0, 10);
				}

				return entry;
			});

		if (reviews.length) {
			data.review = reviews;
		}

		script.textContent = JSON.stringify(data);
	}

	// ==============================================================================================
	// Write a review
	//
	// The panel has two modes. "list" is everything above; "form" and its "success" state are
	// below. The header and the pinned footer belong to the drawer rather than to this element, so
	// they are slotted siblings and are reached through it.
	// ==============================================================================================

	setupForm() {
		this.form = this.querySelector('[data-form]');
		this.success = this.querySelector('[data-success]');
		this.successPhotos = this.querySelector('[data-success-photos]');
		this.formError = this.querySelector('[data-form-error]');
		this.photoInput = this.querySelector('[data-photos]');
		this.photoList = this.querySelector('[data-photo-list]');

		this.backButton = this.drawer?.querySelector('[data-reviews-back]');
		this.listTitle = this.drawer?.querySelector('[data-reviews-list-title]');
		this.formTitle = this.drawer?.querySelector('[data-reviews-form-title]');
		this.openFormButton = this.drawer?.querySelector('[data-reviews-open-form]');
		this.submitButton = this.drawer?.querySelector('[data-reviews-submit]');
		this.doneButton = this.drawer?.querySelector('[data-reviews-done]');

		if (!this.form) {
			return;
		}

		this.openFormButton?.addEventListener('click', () => this.setMode('form'));
		this.backButton?.addEventListener('click', () => this.setMode('list'));
		this.doneButton?.addEventListener('click', () => this.setMode('list'));

		this.form.addEventListener('submit', (event) => this.submitReview(event));
		this.photoInput?.addEventListener('change', () => this.addPhotos());

		this.photoList?.addEventListener('click', (event) => {
			const button = event.target.closest('[data-remove-photo]');

			if (button) {
				this.photos.splice(Number(button.dataset.removePhoto), 1);
				this.renderPhotos();
			}
		});

		// A review that has been sent and acknowledged is finished with, so closing the panel puts
		// it back to the list. A half-typed one is not, so that is left exactly as it was for
		// whenever the customer comes back to it
		this.drawer?.addEventListener('dialog:after-hide', () => {
			if (this.mode === 'success') {
				this.setMode('list');
			}
		});
	}

	setMode(mode) {
		if (!this.form) {
			return;
		}

		this.mode = mode;

		const writing = mode !== 'list';

		if (this.drawer) {
			this.drawer.dataset.reviewsMode = mode;
		}

		this.reading.hidden = writing;
		this.form.hidden = mode !== 'form';
		this.success.hidden = mode !== 'success';

		this.toggleHidden(this.backButton, mode !== 'form');
		this.toggleHidden(this.listTitle, writing);
		this.toggleHidden(this.formTitle, !writing);
		this.toggleHidden(this.openFormButton, writing);
		this.toggleHidden(this.submitButton, mode !== 'form');
		this.toggleHidden(this.doneButton, mode !== 'success');

		this.scrollIntoView({ block: 'start' });

		// Deferred by a frame: opening the panel and switching to the form happen together when
		// the trigger is the "be the first" link, and the drawer moves focus itself as it opens
		const target = { list: this.openFormButton, form: this.backButton, success: this.doneButton }[mode];

		requestAnimationFrame(() => target?.focus());
	}

	toggleHidden(element, hidden) {
		if (element) {
			element.hidden = hidden;
		}
	}

	async submitReview(event) {
		event.preventDefault();

		if (this.submitting) {
			return;
		}

		// A filled honeypot is a bot. It gets the same success state as a person does, rather than
		// an error telling it which field gave it away
		if (this.form.elements.website?.value) {
			this.showSuccess(false);

			return;
		}

		this.hideFormError();

		if (!this.form.reportValidity()) {
			return;
		}

		const data = new FormData(this.form);
		const content = (data.get('review_content') || '').trim();
		const title = (data.get('review_title') || '').trim();

		const payload = {
			appkey: this.appKey,

			// The Shopify product ID, the same value that identifies the product as "domain_key"
			// everywhere else in this integration. Yotpo's reference only calls it "Product
			// unique ID", and the endpoint answers 200 whatever it is sent, so this was confirmed
			// by submitting one review and finding it against the right product in Yotpo's admin
			sku: this.productId,
			product_title: this.productTitle,
			product_url: this.productUrl,
			display_name: (data.get('display_name') || '').trim(),
			email: (data.get('email') || '').trim(),
			review_content: content,

			// Yotpo requires a title in the payload even though the form does not ask for one, so
			// the opening words of the review stand in — which is what Yotpo does for its own
			// untitled reviews. reviewTitle() suppresses exactly that shape when rendering, so a
			// stand-in never shows up in the panel as a truncated duplicate of the review below it
			review_title: title || content.slice(0, 60).trim(),
			review_score: Number(data.get('review_score')),
		};

		this.setSubmitting(true);

		try {
			const request = await fetch(CREATE_REVIEW_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
			});

			if (!request.ok) {
				throw new Error(`Yotpo responded with ${request.status}`);
			}

			const result = await request.json();
			const photosFailed = this.photos.length > 0 && !(await this.uploadPhotos(result.image_upload_token));

			this.showSuccess(photosFailed);
		} catch (error) {
			this.showFormError(
				'Your review could not be sent right now. Please check your connection and try again.'
			);
		} finally {
			this.setSubmitting(false);
		}
	}

	// Photos go up in three steps, the same three Yotpo's own widget makes: have Yotpo sign an S3
	// policy, POST each photo straight to the bucket with it, then hand Yotpo the resulting public
	// URLs against the token the new review came back with.
	//
	// One /images/process call per photo, as the widget does. Yotpo's reference documents
	// image_urls as an array and it does accept several, but a single call for several photos is
	// not a path their own code takes, and the response gives no way to check what actually
	// attached — it answers 200 even to a token that was never issued.
	//
	// Returns false rather than throwing: the review itself is already saved by this point, so a
	// failure here changes what the success state says rather than undoing anything.
	async uploadPhotos(token) {
		if (!token) {
			return false;
		}

		try {
			const policy = await this.fetchUploadPolicy();
			const urls = await Promise.all(
				this.photos.map((file, index) => this.uploadPhoto(policy, token, file, index))
			);

			await Promise.all(
				urls.map(async (url) => {
					const request = await fetch(IMAGE_PROCESS_URL, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ image_upload_token: token, image_urls: [url] }),
					});

					if (!request.ok) {
						throw new Error(`Yotpo responded with ${request.status}`);
					}
				})
			);

			return true;
		} catch (error) {
			return false;
		}
	}

	// The policy is signed for this app key and expires fifteen minutes after it is issued, so it
	// is fetched at upload time rather than when the form opens
	async fetchUploadPolicy() {
		const body = new URLSearchParams();

		body.append('policy_name', 'ReviewImages');
		body.append('app_key', this.appKey);

		const request = await fetch(IMAGE_POLICY_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body,
		});

		if (!request.ok) {
			throw new Error(`Yotpo responded with ${request.status}`);
		}

		return request.json();
	}

	async uploadPhoto(policy, token, file, index) {
		const prepared = await preparePhoto(file);
		const origin = `https://${policy.bucket}.s3.amazonaws.com`;

		// The policy only signs a key starting with the path it came back with, so everything
		// after that is ours. The index keeps two photos picked in the same millisecond apart
		const key = `${policy.path}${token}/${Date.now()}-${index}_${prepared.name}`;
		const body = new FormData();

		body.append('key', key);
		body.append('acl', 'public-read');
		body.append('Content-Type', prepared.type);
		body.append('policy', policy.encoded_policy);
		body.append('x-amz-credential', policy.credential);
		body.append('x-amz-algorithm', S3_ALGORITHM);
		body.append('x-amz-date', policy.date);
		body.append('x-amz-meta-uuid', S3_META_UUID);
		body.append('x-amz-signature', policy.signature);
		body.append('file', prepared.blob, prepared.name);

		// Deliberately no Content-Type header: the browser has to set that one itself so the
		// multipart boundary goes with it
		const request = await fetch(origin, { method: 'POST', body });

		if (!request.ok) {
			throw new Error(`S3 responded with ${request.status}`);
		}

		return `${origin}/${key}`;
	}

	addPhotos() {
		const chosen = Array.from(this.photoInput.files || []);
		const rejected = [];

		// Cleared so that removing a photo and picking the same file again still fires a change
		this.photoInput.value = '';

		chosen.forEach((file) => {
			if (this.photos.length >= MAX_PHOTOS) {
				rejected.push(`${file.name} — no more than ${MAX_PHOTOS} photos`);
			} else if (file.size > MAX_PHOTO_BYTES) {
				rejected.push(`${file.name} — larger than 15MB`);
			} else {
				this.photos.push(file);
			}
		});

		if (rejected.length) {
			this.showFormError(`These photos could not be added: ${rejected.join('; ')}.`);
		} else {
			this.hideFormError();
		}

		this.renderPhotos();
	}

	renderPhotos() {
		// Revoked before the list is rebuilt, or every pick leaks the previous previews
		(this.photoUrls || []).forEach((url) => URL.revokeObjectURL(url));
		this.photoUrls = this.photos.map((file) => URL.createObjectURL(file));

		this.photoList.innerHTML = this.photos
			.map(
				(file, index) => `
					<li class="product-reviews__photo">
						<img src="${this.photoUrls[index]}" alt="" width="64" height="64">
						<button class="product-reviews__photo-remove" type="button" data-remove-photo="${index}">
							${this.removeIcon()}
							<span class="sr-only">Remove ${escapeHtml(file.name)}</span>
						</button>
					</li>
				`
			)
			.join('');

		this.photoList.hidden = this.photos.length === 0;
	}

	removeIcon() {
		return this.querySelector('template[data-icon="remove"]')?.innerHTML || '';
	}

	setSubmitting(submitting) {
		this.submitting = submitting;
		this.form.toggleAttribute('submitting', submitting);

		if (this.submitButton) {
			this.submitButton.disabled = submitting;
			this.submitButton.textContent = submitting ? 'Sending…' : 'Submit review';
		}
	}

	showSuccess(photosFailed) {
		this.form.reset();
		this.photos = [];
		this.renderPhotos();
		this.hideFormError();

		this.successPhotos.hidden = !photosFailed;
		this.successPhotos.textContent = photosFailed
			? 'Your photos could not be uploaded, but your review was sent without them.'
			: '';

		this.setMode('success');
	}

	showFormError(message) {
		this.formError.textContent = message;
		this.formError.hidden = false;
	}

	hideFormError() {
		this.formError.textContent = '';
		this.formError.hidden = true;
	}

	setBusy(busy, append) {
		this.toggleAttribute('loading', busy);

		if (append) {
			this.loadMoreButton.disabled = busy;
			this.loadMoreButton.textContent = busy ? 'Loading…' : 'Load more';
		}
	}

	renderSummary(bottomline) {
		if (!bottomline || !bottomline.total_review) {
			return;
		}

		const { average_score: average, total_review: totalReviews, star_distribution: distribution = {} } = bottomline;

		this.bottomline = bottomline;
		this.updateStructuredData();

		if (this.countLabel) {
			this.countLabel.textContent = `(${totalReviews})`;
			this.countLabel.hidden = false;
		}

		const bars = [];

		for (let score = STAR_COUNT; score >= 1; score -= 1) {
			const count = distribution[score] || 0;
			const percentage = totalReviews ? Math.round((count / totalReviews) * 100) : 0;

			bars.push(`
				<div class="product-reviews__bar-row">
					<span class="product-reviews__bar-label">${score}${this.starIcon('full')}</span>
					<span class="product-reviews__bar"><span class="product-reviews__bar-fill" style="width: ${percentage}%"></span></span>
					<span class="product-reviews__bar-count">${count}</span>
				</div>
			`);
		}

		this.summary.innerHTML = `
			<div class="product-reviews__score">
				<span class="product-reviews__score-value">${average.toFixed(1)}</span>
				<span class="rating-badge__stars" role="img" aria-label="${average.toFixed(1)} out of ${STAR_COUNT} stars">${this.starsMarkup(average)}</span>
				<span class="product-reviews__score-count text-subdued">Based on ${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}</span>
			</div>
			<div class="product-reviews__bars">${bars.join('')}</div>
		`;

		this.summary.hidden = false;
	}

	render(response) {
		if (this.reviews.length === 0) {
			this.list.innerHTML = '';
			this.loadMoreButton.hidden = true;
			this.renderEmpty();

			return;
		}

		const grouping = response.grouping_data || {};
		const markup = this.reviews.map((review) => this.reviewMarkup(review, grouping)).join('');

		this.list.innerHTML = markup;
		this.status.textContent = '';
		this.loadMoreButton.hidden = this.reviews.length >= this.total;
	}

	renderEmpty() {
		if (this.state.search) {
			this.status.innerHTML = `No reviews found for “${escapeHtml(this.state.search)}”. Try searching for something else, or <button type="button" class="link" data-clear-search>clear the search</button>.`;

			this.status.querySelector('[data-clear-search]').addEventListener('click', () => {
				this.searchInput.value = '';
				this.apply({ search: '' });
			});

			return;
		}

		this.status.textContent = this.state.score
			? 'No reviews with this rating yet.'
			: 'There are no reviews for this product yet.';
	}

	renderError() {
		this.list.innerHTML = '';
		this.loadMoreButton.hidden = true;
		this.status.textContent = 'Reviews could not be loaded right now. Please try again.';
	}

	reviewMarkup(review, grouping) {
		const grouped = grouping[review.id];
		const title = this.reviewTitle(review);
		const images = (review.images_data || [])
			.map(
				(image) =>
					`<button type="button" class="product-reviews__image" data-review-image="${escapeHtml(image.original_url)}">
						<img src="${escapeHtml(image.thumb_url)}" alt="" loading="lazy" width="80" height="80">
					</button>`
			)
			.join('');

		return `
			<article class="product-reviews__review">
				<div class="product-reviews__review-head">
					<span class="rating-badge__stars" role="img" aria-label="${review.score} out of ${STAR_COUNT} stars">${this.starsMarkup(review.score)}</span>
					<time class="product-reviews__date text-subdued" datetime="${escapeHtml(review.created_at)}">${formatDate(review.created_at)}</time>
				</div>

				${title ? `<h3 class="product-reviews__review-title h6">${escapeHtml(title)}</h3>` : ''}
				${review.content ? `<p class="product-reviews__review-content">${escapeHtml(review.content)}</p>` : ''}
				${images ? `<div class="product-reviews__images">${images}</div>` : ''}

				<p class="product-reviews__meta text-subdued">
					<span class="product-reviews__author">${escapeHtml(review.user?.display_name || 'Anonymous')}</span>
					${review.verified_buyer ? '<span class="product-reviews__verified">Verified buyer</span>' : ''}
					${grouped ? `<span class="product-reviews__grouped">${escapeHtml(grouped.product_name)}</span>` : ''}
				</p>
			</article>
		`;
	}

	// Stars are cloned from the templates in the panel so they match the rest of the theme,
	// rather than being written out as SVG here. The half-star thresholds are the ones
	// snippets/product-rating.liquid uses, so a 4.4 reads the same in both places
	// Reviews left without a title get one generated by Yotpo from the opening words of the
	// body, which reads as a truncated duplicate ("Lovely little bag for my"). Only a title
	// the customer actually wrote is worth a heading
	reviewTitle(review) {
		const title = (review.title || '').trim();
		const content = (review.content || '').trim();

		if (!title || content.toLowerCase().startsWith(title.toLowerCase())) {
			return '';
		}

		return title;
	}

	starIcon(type) {
		if (!this.starTemplates) {
			this.starTemplates = {};

			Array.from(this.querySelectorAll('template[data-star]')).forEach((template) => {
				this.starTemplates[template.dataset.star] = template.innerHTML;
			});
		}

		return this.starTemplates[type] || '';
	}

	starsMarkup(score) {
		const decimal = score % 1;
		let partial = 0;

		if (decimal >= 0.3 && decimal <= 0.7) {
			partial = 0.5;
		} else if (decimal > 0.7) {
			partial = 1;
		}

		let markup = '';

		for (let index = 1; index <= STAR_COUNT; index += 1) {
			if (index <= score) {
				markup += this.starIcon('full');
			} else if (partial === 0.5) {
				markup += this.starIcon('half');
				partial = 0;
			} else if (partial === 1) {
				markup += this.starIcon('full');
				partial = 0;
			} else {
				markup += this.starIcon('empty');
			}
		}

		return markup;
	}
}

if (!customElements.get('product-reviews')) {
	customElements.define('product-reviews', ProductReviews);
}

// The star rating under the product title is a Yotpo app block, and Yotpo binds its own
// "scroll to the reviews widget" handler to it. Capture phase gets in first so the click
// opens the panel instead.
//
// The panel is matched on the product ID Yotpo already carries on the widget wrapper. The
// same widget instance renders on collection product cards, where it sits inside the link
// to the product: no panel is rendered for those, so they fall through to Yotpo untouched.
function panelFor(target) {
	const widget = target.closest?.('.yotpo-reviews-star-ratings-widget');

	if (!widget || widget.closest('.ProductItem, .product-card')) {
		return null;
	}

	const productId = widget.closest('[data-yotpo-product-id]')?.dataset.yotpoProductId;

	return (productId && document.getElementById(`product-reviews-${productId}`)) || null;
}

function onStarRatingActivate(event) {
	const panel = panelFor(event.target);

	if (!panel) {
		return;
	}

	event.preventDefault();
	event.stopPropagation();

	// Which rating opened it, so that only that one takes focus back when it closes. The panel is
	// opened from more than one place now — the trusted reviews section under the product is the
	// other — and those are left to the focus trap, which returns focus where it found it
	panel.reviewsOpener = event.target.closest('[data-reviews-trigger]') || event.target;

	panel.show();
}

document.addEventListener('click', onStarRatingActivate, { capture: true });

document.addEventListener(
	'keydown',
	(event) => {
		if ((event.key === 'Enter' || event.key === ' ') && event.target.matches?.('[data-reviews-trigger]')) {
			onStarRatingActivate(event);
		}
	},
	{ capture: true }
);

// Yotpo renders the rating asynchronously as a plain div, with no keyboard affordance of its
// own. That was survivable while the rating only scrolled the page, but the panel is now the
// only way to read the reviews, so the widget is upgraded to a real button once it appears.
function upgradeStarRatings() {
	Array.from(document.querySelectorAll('.yotpo-reviews-star-ratings-widget')).forEach((widget) => {
		const panel = panelFor(widget);

		if (!panel || widget.hasAttribute('data-reviews-upgraded')) {
			return;
		}

		const trigger = widget.querySelector('.yotpo-bottom-line-scroll-panel') || widget;

		widget.setAttribute('data-reviews-upgraded', '');
		trigger.setAttribute('data-reviews-trigger', '');
		trigger.setAttribute('role', 'button');
		trigger.setAttribute('tabindex', '0');
		trigger.setAttribute('aria-haspopup', 'dialog');
		trigger.setAttribute('aria-expanded', 'false');

		panel.addEventListener('dialog:after-show', () => trigger.setAttribute('aria-expanded', 'true'));
		panel.addEventListener('dialog:after-hide', () => {
			trigger.setAttribute('aria-expanded', 'false');

			// Only when this rating is what opened it. after-hide runs once the panel has finished
			// closing, which is after the focus trap has put focus back where it came from, so
			// focusing unconditionally here used to drag the page back up to the rating from
			// wherever the visitor actually opened it
			if (panel.reviewsOpener === trigger) {
				trigger.focus();
			}

			panel.reviewsOpener = null;
		});
	});
}

// The Reviews row in the product tabs carries the same rating as the one under the product title.
// The stars are cloned from Yotpo's markup rather than rebuilt from the theme's own star icons, so
// the two readings of one score are the same shape and fill exactly alike — Yotpo shows a 4.8 as
// five stars, and so does the row.
//
// Only the colour changes, and it changes through the gradient each star is painted with rather
// than by overriding the fill, so a part-filled star keeps its fraction. Yotpo draws the outline
// and the filled part in the review colour and leaves the rest white, so the outline is what says
// which colour to replace.
function copyRating(widget, target, panel) {
	const stars = Array.from(widget.querySelectorAll('.yotpo-sr-bottom-line-summary .star-container'));

	if (!stars.length) {
		return;
	}

	const starColour = widget.querySelector('.star-container path[stroke]')?.getAttribute('stroke');

	target.replaceChildren(
		...stars.map((star, index) => {
			const copy = star.cloneNode(true);

			// The gradient ids come over with the clone, and a duplicate id resolves to whichever
			// comes first in the document — which is Yotpo's own, still gold. Each copy gets its own
			copy.querySelectorAll('linearGradient[id]').forEach((gradient, gradientIndex) => {
				const from = gradient.id;
				const to = `${panel.id}-star-${index}-${gradientIndex}`;

				gradient.id = to;

				// Written by Yotpo as url('#id'), which is not the shape a rewrite would produce,
				// so both are matched
				copy.querySelectorAll('[fill]').forEach((node) => {
					if (node.getAttribute('fill').replace(/['"]/g, '') === `url(#${from})`) {
						node.setAttribute('fill', `url(#${to})`);
					}
				});
			});

			copy.querySelectorAll('path[stroke]').forEach((path) => path.setAttribute('stroke', 'currentColor'));

			// Without an outline to match against, the first stop of each gradient is the filled
			// part and the one after it is the white behind it
			copy.querySelectorAll('stop').forEach((stop, stopIndex) => {
				const matches = starColour ? stop.getAttribute('stop-color') === starColour : stopIndex === 0;

				if (matches) {
					stop.setAttribute('stop-color', 'currentColor');
				}
			});

			return copy;
		})
	);

	const score = widget.querySelector('.yotpo-sr-bottom-line-score')?.textContent.trim();

	if (score) {
		target.setAttribute('role', 'img');
		target.setAttribute('aria-label', `${score} out of ${STAR_COUNT} stars`);
	} else {
		target.setAttribute('aria-hidden', 'true');
	}

	target.hidden = false;
}

// Filled once per row. The row is revealed rather than hidden, so "still hidden" is also what marks
// one as waiting for a rating, and a product with no reviews simply keeps its plain Reviews row
function copyRatingsToTabs() {
	Array.from(document.querySelectorAll('.yotpo-reviews-star-ratings-widget')).forEach((widget) => {
		const panel = panelFor(widget);
		const target = panel && document.querySelector(`[aria-controls="${panel.id}"] [data-reviews-rating][hidden]`);

		if (target) {
			copyRating(widget, target, panel);
		}
	});
}

// The widget can be re-rendered by Yotpo (a variant change re-runs it), so the observer stays
// active rather than disconnecting on the first hit. The guards keep the callback cheap
const starRatingObserver = new MutationObserver(() => {
	if (document.querySelector('.yotpo-reviews-star-ratings-widget:not([data-reviews-upgraded])')) {
		upgradeStarRatings();
	}

	if (document.querySelector('[data-reviews-rating][hidden]')) {
		copyRatingsToTabs();
	}
});

document.addEventListener('DOMContentLoaded', upgradeStarRatings);
upgradeStarRatings();
copyRatingsToTabs();
starRatingObserver.observe(document.body, { childList: true, subtree: true });
